/**
 * Geofence auto-prompt hook.
 * - Watches the user's geolocation continuously while logged in.
 * - Compares against the active job's site coordinates + radius.
 * - When the user ENTERS the radius and isn't clocked in, fires a toast with "Sign in" action.
 * - When the user LEAVES the radius and is currently clocked in, fires a toast with "Sign out" action.
 *
 * Tracks state in component memory so we don't double-fire the same prompt.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Haversine distance in metres
function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export const useGeofenceAutoPrompt = ({ user, enabled = true }) => {
  const watchIdRef = useRef(null);
  const inRadiusRef = useRef({}); // { jobId: bool } — last known state per job
  const lastPromptRef = useRef({}); // { 'enter:jobId': ts } debounce key
  const [position, setPosition] = useState(null);
  const [trackedJobs, setTrackedJobs] = useState([]); // jobs with a geofence
  const [clockStatus, setClockStatus] = useState(null);

  // Refresh tracked jobs + clock status on a slow interval
  useEffect(() => {
    if (!enabled || !user) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        // Fetch user's relevant jobs (assigned + scheduled today)
        const [jobsRes, clockRes] = await Promise.all([
          axios.get(`${API}/jobs?status=in_progress`).catch(() => ({ data: [] })),
          axios.get(`${API}/time/clock-status`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const jobs = (jobsRes.data || []).filter(
          (j) => j.site_latitude && j.site_longitude
        );
        setTrackedJobs(jobs);
        setClockStatus(clockRes.data);
      } catch (e) {
        // silent
      }
    };
    refresh();
    const id = setInterval(refresh, 60_000); // every minute
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, user]);

  // Start geolocation watch
  useEffect(() => {
    if (!enabled || !user || !navigator.geolocation) return;
    if (watchIdRef.current !== null) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        // Permission denied or unavailable — silent. User has to opt in.
        console.warn("Geofence: geolocation unavailable", err.message);
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 60_000 }
    );

    return () => {
      if (watchIdRef.current !== null) {
        try {
          navigator.geolocation.clearWatch(watchIdRef.current);
        } catch (_) {}
        watchIdRef.current = null;
      }
    };
  }, [enabled, user]);

  // React to position changes — fire prompts
  useEffect(() => {
    if (!position || !user) return;

    const now = Date.now();
    const DEBOUNCE_MS = 5 * 60 * 1000; // 5 min between same prompts

    for (const job of trackedJobs) {
      const radius = job.geofence_radius_m || job.default_geofence_radius || 100;
      const dist = distanceMeters(
        position.lat,
        position.lng,
        job.site_latitude,
        job.site_longitude
      );
      const inside = dist <= radius;
      const wasInside = inRadiusRef.current[job.id] === true;
      inRadiusRef.current[job.id] = inside;

      // ENTER event
      if (inside && !wasInside) {
        const isClockedInHere =
          clockStatus?.clocked_in && clockStatus?.job_id === job.id;
        if (!isClockedInHere) {
          const key = `enter:${job.id}`;
          const last = lastPromptRef.current[key] || 0;
          if (now - last > DEBOUNCE_MS) {
            lastPromptRef.current[key] = now;
            toast(`📍 You've arrived at ${job.title}`, {
              description: "Want to clock in to this job?",
              duration: 30_000,
              action: {
                label: "Clock In",
                onClick: async () => {
                  try {
                    await axios.post(`${API}/time/clock-in`, {
                      job_id: job.id,
                      latitude: position.lat,
                      longitude: position.lng,
                      auto_geofence: true,
                    });
                    toast.success(`Clocked in to ${job.title}`);
                  } catch (e) {
                    toast.error(
                      e.response?.data?.detail || "Failed to clock in"
                    );
                  }
                },
              },
            });
          }
        }
      }

      // LEAVE event
      if (!inside && wasInside) {
        const isClockedInHere =
          clockStatus?.clocked_in && clockStatus?.job_id === job.id;
        if (isClockedInHere) {
          const key = `leave:${job.id}`;
          const last = lastPromptRef.current[key] || 0;
          if (now - last > DEBOUNCE_MS) {
            lastPromptRef.current[key] = now;
            toast(`👋 You've left ${job.title}`, {
              description: "Don't forget to clock out!",
              duration: 30_000,
              action: {
                label: "Clock Out",
                onClick: async () => {
                  try {
                    await axios.post(`${API}/time/clock-out`, {
                      latitude: position.lat,
                      longitude: position.lng,
                      auto_geofence: true,
                    });
                    toast.success(`Clocked out of ${job.title}`);
                  } catch (e) {
                    toast.error(
                      e.response?.data?.detail || "Failed to clock out"
                    );
                  }
                },
              },
            });
          }
        }
      }
    }
  }, [position, trackedJobs, clockStatus, user]);

  return { position, trackedJobs, clockStatus };
};

export default useGeofenceAutoPrompt;
